/*
 * Copyright © 2026 Medaica, Inc
 *
 * All rights reserved.
 *
 * This code is confidential and proprietary information belonging to Medaica, Inc.
 * Unauthorized copying, distribution, or use of this code, in whole or in part,
 * is strictly prohibited, and may constitute a violation of intellectual property rights.
 *
 * If you have received this code in error, please notify the owner immediately
 * at support@medaica.com and delete this file from your system.
 */

/* eslint-disable @typescript-eslint/no-unsafe-argument --
 * The report document and pcg-report core are loose-typed by design: the
 * core is vendored prototype JS with a hand-written surface .d.ts. */

import React from "react"
// @ts-ignore vendored plain-JS core with hand-written surface types
import { reportFeatures, currentHeartRateBpm } from "./core"
import {
  PROVIDER_REPORT_DISCLAIMER_TEXT,
  PROVIDER_REPORT_FINDINGS_EMPTY_TEXT,
  PROVIDER_REPORT_TITLE,
  providerFindingOf,
  providerReportTexts,
} from "./provider-report-document"

/**
 * Provider prose clinical document — content-fidelity port of the
 * prototype's `renderProviderDocument()` (cardiac_report_v0_5_6_dev_2.html
 * L4862-4939). All content
 * generation goes through the extracted core (verbatim provenance per
 * MODULE-MAP): result text, HR-adjusted reference labels, flag vocabulary
 * (green → no flag), interpretation branches incl. the authored
 * per-feature provider texts and AHA/ACC 2019 references, synthesized
 * summary/recommendations with the prototype's staleness rules, and the
 * per-finding "Clinician note:" gated on reviewer-edited notes
 * (providerClinicianNoteHTML L5472-5480, document mode).
 *
 * Faithful upstream semantics worth naming:
 * - Colors come from RAW getColorClass — not effectiveFeatureColorClass —
 *   so a reviewer-dragged marker does not re-grade the prose document
 *   (prototype L4877; cards/list deliberately differ).
 * - Title is "PCG Cardiac Screening Report" (no "— Provider Copy"; that
 *   suffix exists only in the prototype's dead renderSummary()).
 * - Recommendations effectively always renders (providerRecommendationsText
 *   never returns empty); Reviewing Clinician Notes only when providerNotes
 *   is non-blank; findings fallback "No findings available."
 *
 * Deliberate deviations from the prototype:
 * - Demographics come from the PLATFORM (the `demographics` prop — the
 *   prototype read its own editable header inputs, defaulting to "Coded
 *   patient" placeholders) and the document date is the EXAM date — the
 *   prototype prints today's date (its pinned bug, L4872).
 * - React-escaped interpolation (the prototype interpolated raw HTML),
 *   CSS-grid demographics instead of the prototype's global-CSS-bled
 *   <table>. There is deliberately no recordings section — the prototype
 *   document has none.
 *
 * The content derivations live in provider-report-document, shared with the
 * builder of the provider report PDF's data so the PDF cannot drift from
 * this view.
 */

interface ProviderDemographics {
  patientName?: string
  dob?: string
  age?: string
  sex?: string
  patientId?: string
  examDate?: string
}

interface FeatureLike {
  name: string
  [key: string]: unknown
}

export interface ProviderReportLike {
  summary?: { patientText?: string; providerText?: string }
  followUpNote?: { patientText?: string; providerText?: string }
  providerNotes?: string
  features: FeatureLike[]
}

const DASH = "—"
// Two literal non-breaking spaces — the prototype's &nbsp;&nbsp; separators
const SEP = "  "

const SectionTitle: React.FC<{ children: string }> = ({ children }) => (
  <div className="phr-provider-section-title">{children}</div>
)

export const HeartReportProviderDocument: React.FC<{
  report: ProviderReportLike
  demographics?: ProviderDemographics
}> = ({ report, demographics = {} }) => {
  const features = report.features || []
  const hr = currentHeartRateBpm(features)
  const visible = reportFeatures(features, { viewMode: "provider" })
  const { summaryText, recommendationsText, clinicianNotesText } = providerReportTexts(report, features, hr)

  return (
    <div className="phr-provider-doc" data-testid="heart-report-provider">
      <div className="phr-provider-head">
        <div className="phr-provider-title">{PROVIDER_REPORT_TITLE}</div>
        <div className="phr-provider-demographics" data-testid="provider-demographics">
          <span>
            <strong>Patient:</strong> {demographics.patientName || "Coded patient"}
          </span>
          <span>
            <strong>Date:</strong> {demographics.examDate || DASH}
          </span>
          <span>
            <strong>DOB:</strong> {demographics.dob || DASH}
            {SEP}
            <strong>Age:</strong> {demographics.age || DASH}
            {SEP}
            <strong>Sex:</strong> {demographics.sex || DASH}
          </span>
          <span>
            <strong>ID:</strong> {demographics.patientId || DASH}
          </span>
        </div>
      </div>

      <div className="phr-provider-section">
        <SectionTitle>Findings</SectionTitle>
        {visible.length === 0 ? (
          <div style={{ color: "#666", fontStyle: "italic" }}>{PROVIDER_REPORT_FINDINGS_EMPTY_TEXT}</div>
        ) : (
          visible.map((feature: FeatureLike) => {
            const {
              title,
              resultText,
              referenceText: refText,
              flagText,
              reviewText,
              interpretation,
              clinicianNote,
            } = providerFindingOf(feature, features, hr)
            return (
              <div className="phr-provider-finding" data-testid={`provider-finding-${feature.name}`} key={feature.name}>
                <div className="phr-provider-finding-name">{title}</div>
                {(resultText || refText || flagText) && (
                  <div className="phr-provider-finding-result">
                    {resultText ? (
                      <>
                        {resultText}
                        {refText ? (
                          <>
                            {SEP}
                            <span className="phr-provider-ref">({refText})</span>
                          </>
                        ) : null}
                      </>
                    ) : refText ? (
                      // Prototype: without result text the reference renders
                      // unparenthesized (L4884)
                      <span className="phr-provider-ref">{refText}</span>
                    ) : null}
                    {flagText ? (
                      <>
                        {SEP}
                        <span className="phr-provider-flag">{flagText}</span>
                      </>
                    ) : null}
                  </div>
                )}
                {reviewText ? (
                  <div className="phr-provider-review-flag" data-testid={`provider-review-flag-${feature.name}`}>
                    {reviewText}
                  </div>
                ) : null}
                <div className="phr-provider-interpretation">{interpretation}</div>
                {clinicianNote && (
                  <div className="phr-provider-clinician-note">
                    <strong>Clinician note:</strong> {clinicianNote}
                  </div>
                )}
              </div>
            )
          })
        )}
      </div>

      <div className="phr-provider-section">
        <SectionTitle>Summary</SectionTitle>
        <div>{summaryText}</div>
      </div>

      {recommendationsText ? (
        <div className="phr-provider-section">
          <SectionTitle>Recommendations</SectionTitle>
          <div>{recommendationsText}</div>
        </div>
      ) : null}

      {clinicianNotesText ? (
        <div className="phr-provider-section">
          <SectionTitle>Reviewing Clinician Notes</SectionTitle>
          <div>{clinicianNotesText}</div>
        </div>
      ) : null}

      <div className="phr-provider-disclaimer">{PROVIDER_REPORT_DISCLAIMER_TEXT}</div>
    </div>
  )
}

export default HeartReportProviderDocument
